<script setup>
/* eslint-disable no-console */
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import ServiceOrdersAPI from 'dashboard/api/serviceOrders';
import OrderConfigurationsAPI from 'dashboard/api/orderConfigurations';
import {
  refreshDataJson,
  consultaDetalleContratoJson,
  getPuntoServicioPorContador,
  crearOrdenTrabajo,
  xmlToJson,
} from 'dashboard/api/cea';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Avatar from 'next/avatar/Avatar.vue';
import ReportVisitModal from './ReportVisitModal.vue';
import ResolveOrderModal from './ResolveOrderModal.vue';
import CancelOrderDialog from './CancelOrderDialog.vue';
import MergeOrderModal from './MergeOrderModal.vue';
import RedirectOrderModal from './RedirectOrderModal.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAlert } from 'dashboard/composables';

const { t } = useI18n();
const { isAdmin } = useAdmin();
const router = useRouter();
const route = useRoute();

const order = ref(null);
const isLoading = ref(true);
const isUpdatingStatus = ref(false);
const isEditingObservations = ref(false);
const editedObservations = ref('');
const isSavingObservations = ref(false);

const aquasisData = ref(null);
const isLoadingAquasis = ref(false);
const showReportVisitModal = ref(false);
const showResolveOrderModal = ref(false);
const showCancelOrderDialog = ref(false);
const showMergeOrderModal = ref(false);
const showRedirectOrderModal = ref(false);
const aquasisError = ref(null);
const fieldVisibility = ref({});
const isResyncing = ref(false);
const resyncError = ref(null);
const manualIdPtoServicio = ref('');
const showManualResyncFields = ref(false);

const orderClassLabel = computed(() => {
  if (order.value?.order_class === 'service_order')
    return t('ORDERS.SERVICE_ORDER');
  return t('ORDERS.WORK_ORDER');
});

const orderClassBadge = computed(() => {
  if (order.value?.order_class === 'service_order')
    return t('ORDERS.SERVICE_ORDER_BADGE');
  return t('ORDERS.WORK_ORDER_BADGE');
});

const orderClassColor = computed(() => {
  if (order.value?.order_class === 'service_order')
    return 'bg-n-teal-9/10 text-n-teal-11';
  return 'bg-n-brand/10 text-n-blue-text';
});

const orderTypeCode = computed(() => {
  if (!order.value) return null;
  return order.value.order_type_record?.code || order.value.order_type || null;
});

const visibleSections = computed(() => {
  if (!orderTypeCode.value || !fieldVisibility.value) return {};
  const typeStr = String(orderTypeCode.value);
  return fieldVisibility.value[typeStr] || {};
});

const showSection = key => {
  return visibleSections.value[key] === true;
};

const ensureArray = val => {
  if (!val) return [];
  return Array.isArray(val) ? val : [val];
};

const getStatusColor = status => {
  const colors = {
    draft: 'bg-n-slate-9/10 text-n-slate-11',
    ready: 'bg-n-amber-9/10 text-n-amber-11',
    dispatched: 'bg-n-brand/10 text-n-blue-text',
    en_route: 'bg-violet-100 text-violet-800',
    on_site: 'bg-n-brand/10 text-n-blue-text',
    field_complete: 'bg-n-teal-9/10 text-n-teal-11',
    redirected: 'bg-orange-100 text-orange-800',
    cancelled: 'bg-n-ruby-9/10 text-n-ruby-11',
    merged: 'bg-n-slate-9/10 text-n-slate-11',
  };
  return colors[status] || 'bg-n-slate-9/10 text-n-slate-11';
};

const getStatusLabel = status => {
  const labels = {
    draft: t('ORDERS.STATUS_DRAFT'),
    ready: t('ORDERS.STATUS_READY'),
    dispatched: t('ORDERS.STATUS_DISPATCHED'),
    en_route: t('ORDERS.STATUS_EN_ROUTE'),
    on_site: t('ORDERS.STATUS_ON_SITE'),
    field_complete: t('ORDERS.STATUS_FIELD_COMPLETE'),
    redirected: t('ORDERS.STATUS_REDIRECTED'),
    cancelled: t('ORDERS.STATUS_CANCELLED'),
    merged: t('ORDERS.STATUS_MERGED'),
  };
  return labels[status] || status;
};

const formatDate = date => {
  if (!date) return '-';
  return new Date(date).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fetchOrder = async () => {
  try {
    isLoading.value = true;
    const response = await ServiceOrdersAPI.show(route.params.orderId);
    order.value = response.data;
  } catch (error) {
    console.error('Error fetching order:', error);
  } finally {
    isLoading.value = false;
  }
};

const fetchFieldVisibility = async () => {
  try {
    const response = await OrderConfigurationsAPI.get();
    fieldVisibility.value = response.data;
  } catch (error) {
    console.error('Error fetching field visibility:', error);
  }
};

const recoverExploitationCode = async () => {
  const contractNum = order.value?.ticket?.contract_number?.trim();
  if (!contractNum) return null;

  try {
    const contractDetail = await consultaDetalleContratoJson(contractNum);
    const contrato =
      contractDetail?.GenericoContratoDTO?.contrato ||
      contractDetail?.contrato ||
      {};
    const code = contrato.explotacion || '';
    if (code) {
      await ServiceOrdersAPI.update(order.value.id, {
        aquasis_exploitation_code: code,
      });
      order.value.aquasis_exploitation_code = code;
    }
    return code || null;
  } catch (err) {
    console.error('Error recovering exploitation code:', err);
    return null;
  }
};

const extractSoapFault = errorMessage => {
  if (!errorMessage) return null;
  const match = errorMessage.match(/<faultstring>([^<]+)/);
  return match ? match[1] : null;
};

const fetchAquasisData = async () => {
  if (!order.value?.aquasis_order_id) return;
  try {
    isLoadingAquasis.value = true;
    aquasisError.value = null;

    let exploitCode = order.value.aquasis_exploitation_code;
    if (!exploitCode) {
      exploitCode = await recoverExploitationCode();
    }

    aquasisData.value = await refreshDataJson(
      order.value.aquasis_order_id,
      exploitCode
    );
  } catch (error) {
    console.error('Error fetching Aquasis data:', error);
    const soapFault = extractSoapFault(error?.message) || error?.message || '';
    aquasisError.value = t('ORDERS.AQUASIS.ERROR_GENERIC', {
      message: soapFault || '',
    });
  } finally {
    isLoadingAquasis.value = false;
  }
};

const updateStatus = async newStatus => {
  try {
    isUpdatingStatus.value = true;
    await ServiceOrdersAPI.update(order.value.id, { status: newStatus });
    await fetchOrder();
  } catch (error) {
    console.error('Error updating status:', error);
    const msg = error?.response?.data?.error || t('ORDERS.STATUS_UPDATE_ERROR');
    useAlert(msg);
  } finally {
    isUpdatingStatus.value = false;
  }
};

const startEditingObservations = () => {
  editedObservations.value = order.value.observations || '';
  isEditingObservations.value = true;
};

const cancelEditingObservations = () => {
  isEditingObservations.value = false;
};

const saveObservations = async () => {
  try {
    isSavingObservations.value = true;
    await ServiceOrdersAPI.update(order.value.id, {
      observations: editedObservations.value,
    });
    await fetchOrder();
    isEditingObservations.value = false;
  } catch (error) {
    console.error('Error saving observations:', error);
  } finally {
    isSavingObservations.value = false;
  }
};

const canPerformAquasisActions = computed(() => {
  const activeStatuses = [
    'draft',
    'ready',
    'dispatched',
    'en_route',
    'on_site',
  ];
  return (
    order.value?.aquasis_order_id && activeStatuses.includes(order.value.status)
  );
});

/** Aquasis id absent or blank — agents may pick Cancelada in the status dropdown only in this case */
const isAquasisUnlinked = computed(() => {
  const id = order.value?.aquasis_order_id;
  return !id || !String(id).trim();
});

/** Unlinked order in a non-terminal status — any user (including agents) may cancel */
const canCancelUnlinked = computed(() => {
  if (!isAquasisUnlinked.value) return false;
  const nonTerminal = ['draft', 'ready', 'dispatched', 'en_route', 'on_site', 'field_complete'];
  return nonTerminal.includes(order.value?.status);
});

const canMerge = computed(() => {
  const status = order.value?.status;
  const terminalStatuses = [
    'merged',
    'cancelled',
    'field_complete',
    'redirected',
  ];
  return status && !terminalStatuses.includes(status);
});

const canRedirect = computed(() => {
  const status = order.value?.status;
  const terminalStatuses = [
    'merged',
    'cancelled',
    'field_complete',
    'redirected',
  ];
  return status && !terminalStatuses.includes(status);
});

const onOrderMerged = () => {
  fetchOrder();
};

const onOrderRedirected = newOrder => {
  fetchOrder();
  if (newOrder?.id) {
    router.push({ name: 'order_details', params: { orderId: newOrder.id } });
  }
};

const onVisitReported = () => {
  fetchAquasisData();
};

const onOrderResolved = () => {
  fetchOrder();
  fetchAquasisData();
};

const onOrderCancelled = () => {
  fetchOrder();
  fetchAquasisData();
};

const resyncAquasis = async () => {
  if (isResyncing.value) return;
  const contractNum = order.value?.ticket?.contract_number?.trim();
  if (!contractNum) {
    resyncError.value = t('ORDERS.AQUASIS.ERROR_NO_CONTRACT');
    return;
  }

  try {
    isResyncing.value = true;
    resyncError.value = null;

    // Step 1: Get contract detail for meter serial and exploitation code
    let contractDetail;
    try {
      contractDetail = await consultaDetalleContratoJson(contractNum);
    } catch (error) {
      console.error('Error fetching contract detail:', error);
      resyncError.value = t('ORDERS.AQUASIS.ERROR_CONTRACT_QUERY');
      showManualResyncFields.value = true;
      return;
    }

    const contrato =
      contractDetail?.GenericoContratoDTO?.contrato ||
      contractDetail?.contrato ||
      {};
    const meterSerial = contrato.numeroContador || '';
    const exploitationCode = contrato.explotacion || '';

    // Step 2: Look up idPtoServicio from meter
    let idPtoServicio = '';
    if (meterSerial) {
      try {
        const meterXml = await getPuntoServicioPorContador(
          meterSerial,
          'WSGESTIONDEUDA'
        );
        const meterReturn = meterXml.getElementsByTagName(
          'getPuntoServicioPorContadorReturn'
        )[0];
        if (meterReturn) {
          const meterJson = xmlToJson(meterReturn);
          idPtoServicio =
            meterJson?.puntosServicioContadorDTO?.PuntosServicioContadorDTO
              ?.puntoServicioDTO?.PuntoServicioDTO?.id || '';
        }
      } catch (error) {
        console.error('Error fetching idPtoServicio:', error);
      }
    }

    if (!idPtoServicio && !meterSerial) {
      resyncError.value = t('ORDERS.AQUASIS.ERROR_NO_METER');
      showManualResyncFields.value = true;
    } else if (!idPtoServicio) {
      resyncError.value = t('ORDERS.AQUASIS.ERROR_NO_SERVICE_POINT');
      showManualResyncFields.value = true;
    }

    // Use manual override if provided
    if (manualIdPtoServicio.value?.trim()) {
      idPtoServicio = manualIdPtoServicio.value.trim();
    }

    // Cannot proceed without idPtoServicio
    if (!idPtoServicio) return;

    // Step 3: Build crearOrdenTrabajo payload
    const payload = {
      tipoOrden:
        order.value.order_type_record?.code || order.value.order_type || '',
      motivoOrden: order.value.order_motive?.code || '',
      fechaCreacionOrden: new Date(order.value.request_date).toISOString(),
      numContrato: contractNum,
      idPtoServicio,
      fechaEstimdaFin: new Date(order.value.request_date).toISOString(),
      observaciones: (order.value.observations || '').slice(0, 80),
      codigoReparacion:
        order.value.repair_code_record?.code || order.value.repair_code || '',
      anyoExpediente: String(new Date().getFullYear()),
    };

    const xmlDoc = await crearOrdenTrabajo(payload);

    // Step 4: Parse response
    const returnEl =
      xmlDoc.getElementsByTagName('return')[0] ||
      xmlDoc.getElementsByTagName('crearOrdenTrabajoReturn')[0] ||
      xmlDoc.getElementsByTagName('crearOrdenTrabajoResponse')[0];

    let aquasisOrderId = null;
    if (returnEl) {
      if (returnEl.children && returnEl.children.length > 0) {
        const json = xmlToJson(returnEl);
        aquasisOrderId =
          json?.numOrden ||
          json?.ordenTrabajo ||
          returnEl.textContent?.trim() ||
          null;
      } else {
        aquasisOrderId = returnEl.textContent?.trim() || null;
      }
    }

    if (!aquasisOrderId) {
      resyncError.value = t('ORDERS.AQUASIS.ERROR_NO_ORDER_ID');
      return;
    }

    // Step 5: Update the local order with aquasis ID and exploitation code
    await ServiceOrdersAPI.update(order.value.id, {
      aquasis_order_id: aquasisOrderId,
      aquasis_exploitation_code: exploitationCode,
    });

    // Success — hide manual fields
    showManualResyncFields.value = false;
    manualIdPtoServicio.value = '';

    await fetchOrder();
    fetchAquasisData();
  } catch (error) {
    console.error('Error resyncing Aquasis:', error);
    resyncError.value = t('ORDERS.AQUASIS.ERROR_GENERIC', {
      message: error.message || '',
    });
  } finally {
    isResyncing.value = false;
  }
};

const goBack = () => {
  router.push({ name: 'orders_list' });
};

const goToOrder = orderId => {
  router.push({ name: 'order_details', params: { orderId } });
};

const goToTicket = ticketId => {
  router.push({ name: 'ticket_details', params: { ticketId } });
};

onMounted(async () => {
  await Promise.all([fetchOrder(), fetchFieldVisibility()]);
  if (order.value?.aquasis_order_id) {
    fetchAquasisData();
  }
});
</script>

<template>
  <!-- Loading State -->
  <div
    v-if="isLoading"
    class="flex items-center justify-center w-full h-full min-h-0"
  >
    <Spinner :size="32" />
  </div>

  <!-- Order Details -->
  <div
    v-else-if="order"
    class="flex flex-col w-full h-full min-h-0 bg-n-background"
  >
    <!-- Header -->
    <header
      class="flex items-center justify-between p-4 border-b border-n-weak bg-n-solid-2"
    >
      <div class="flex items-center gap-3">
        <Button variant="ghost" icon="i-lucide-arrow-left" @click="goBack" />
        <div class="flex flex-col">
          <div class="flex items-center gap-2">
            <span class="font-mono text-sm text-n-slate-11">
              #{{ order.id }}
            </span>
            <span
              v-if="order.aquasis_order_id"
              class="px-1.5 py-0.5 text-xs rounded bg-n-brand/10 text-n-blue-text font-mono"
            >
              {{ $t('ORDERS.AQUASIS_COL') }}: {{ order.aquasis_order_id }}
            </span>
          </div>
          <h1 class="text-lg font-semibold text-n-slate-12">
            {{ orderClassLabel }}
          </h1>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <Button
          v-if="order.aquasis_order_id"
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :is-loading="isLoadingAquasis"
          :label="$t('ORDERS.AQUASIS.REFRESH')"
          size="xs"
          @click="fetchAquasisData"
        />
        <Button
          v-if="canPerformAquasisActions"
          variant="ghost"
          icon="i-lucide-clipboard-check"
          :label="$t('ORDERS.AQUASIS.REPORT_VISIT')"
          size="xs"
          @click="showReportVisitModal = true"
        />
        <Button
          v-if="canPerformAquasisActions"
          variant="ghost"
          icon="i-lucide-check-circle"
          :label="$t('ORDERS.AQUASIS.RESOLVE_ORDER')"
          size="xs"
          @click="showResolveOrderModal = true"
        />
        <Button
          v-if="canPerformAquasisActions || canCancelUnlinked"
          variant="ghost"
          icon="i-lucide-x-circle"
          :label="$t('ORDERS.AQUASIS.CANCEL_ORDER')"
          size="xs"
          color-scheme="alert"
          @click="showCancelOrderDialog = true"
        />
        <Button
          v-if="canMerge"
          variant="ghost"
          icon="i-lucide-merge"
          :label="$t('ORDERS.AQUASIS.MERGE_ORDERS')"
          size="xs"
          @click="showMergeOrderModal = true"
        />
        <Button
          v-if="canRedirect"
          variant="ghost"
          icon="i-lucide-arrow-right-left"
          :label="$t('ORDERS.REDIRECT.BUTTON')"
          size="xs"
          @click="showRedirectOrderModal = true"
        />
        <span
          :class="getStatusColor(order.status)"
          class="px-3 py-1 text-sm font-medium rounded-full"
        >
          {{ getStatusLabel(order.status) }}
        </span>
      </div>
    </header>

    <!-- Content -->
    <div class="flex flex-1 overflow-hidden">
      <!-- Main Content -->
      <div class="flex-1 overflow-auto p-4">
        <!-- Aquasis Not Linked Warning -->
        <div
          v-if="!order.aquasis_order_id"
          class="mb-6 p-4 rounded-lg bg-n-amber-3 border border-n-amber-6"
        >
          <div class="flex items-start gap-3">
            <span
              class="i-lucide-alert-triangle text-lg text-n-amber-11 mt-0.5 shrink-0"
            />
            <div class="flex-1">
              <p class="text-sm font-medium text-n-amber-11">
                {{ $t('ORDERS.AQUASIS.NOT_LINKED') }}
              </p>
              <p class="text-xs text-n-amber-11/80 mt-1">
                {{ $t('ORDERS.AQUASIS.NOT_LINKED_DESC') }}
              </p>
              <p v-if="resyncError" class="text-xs text-n-ruby-11 mt-2">
                {{ resyncError }}
              </p>
              <div
                v-if="showManualResyncFields"
                class="mt-3 flex flex-col gap-1"
              >
                <label class="text-sm font-medium text-n-slate-12">
                  {{ $t('ORDERS.AQUASIS.SERVICE_POINT') }}
                </label>
                <input
                  v-model="manualIdPtoServicio"
                  type="text"
                  :placeholder="$t('ORDERS.AQUASIS.SERVICE_POINT_PLACEHOLDER')"
                  class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
                />
              </div>
              <Button
                class="mt-3"
                size="xs"
                icon="i-lucide-refresh-cw"
                :is-loading="isResyncing"
                :label="$t('ORDERS.AQUASIS.RETRY_SYNC')"
                @click="resyncAquasis"
              />
            </div>
          </div>
        </div>

        <!-- Merged Into Banner -->
        <div
          v-if="order.merged_into_id"
          class="mb-6 p-4 rounded-lg bg-n-slate-3 border border-n-slate-6"
        >
          <div class="flex items-start gap-3">
            <span
              class="i-lucide-merge text-lg text-n-slate-11 mt-0.5 shrink-0"
            />
            <div class="flex-1">
              <p class="text-sm font-medium text-n-slate-11">
                {{ $t('ORDERS.MERGE.MERGED_INTO') }}
                <button
                  class="font-mono text-n-brand hover:underline cursor-pointer"
                  @click="goToOrder(order.merged_into_id)"
                >
                  #{{ order.merged_into_id }}
                </button>
              </p>
              <p v-if="order.merge_notes" class="text-xs text-n-slate-10 mt-1">
                {{ order.merge_notes }}
              </p>
            </div>
          </div>
        </div>

        <!-- Redirected To Banner (on original order) -->
        <div
          v-if="order.redirected_to"
          class="mb-6 p-4 rounded-lg bg-orange-50 border border-orange-200 dark:bg-orange-950/20 dark:border-orange-800"
        >
          <div class="flex items-start gap-3">
            <span
              class="i-lucide-arrow-right-left text-lg text-orange-600 dark:text-orange-400 mt-0.5 shrink-0"
            />
            <div class="flex-1">
              <p
                class="text-sm font-medium text-orange-800 dark:text-orange-300"
              >
                {{ $t('ORDERS.REDIRECT.REDIRECTED_TO') }}
                <button
                  class="font-mono text-n-brand hover:underline cursor-pointer"
                  @click="goToOrder(order.redirected_to.id)"
                >
                  #{{ order.redirected_to.id }}
                </button>
                <!-- eslint-disable vue/no-bare-strings-in-template -->
                <span
                  v-if="order.redirected_to.aquasis_order_id"
                  class="ml-1 text-xs font-mono"
                >
                  (Aquasis: {{ order.redirected_to.aquasis_order_id }})
                </span>
                <!-- eslint-enable vue/no-bare-strings-in-template -->
              </p>
              <p
                v-if="order.redirect_notes"
                class="text-xs text-orange-700 dark:text-orange-400/80 mt-1"
              >
                {{ order.redirect_notes }}
              </p>
            </div>
          </div>
        </div>

        <!-- Redirected From Banner (on new order) -->
        <div
          v-if="order.redirected_from_id"
          class="mb-6 p-4 rounded-lg bg-orange-50 border border-orange-200 dark:bg-orange-950/20 dark:border-orange-800"
        >
          <div class="flex items-start gap-3">
            <span
              class="i-lucide-arrow-right-left text-lg text-orange-600 dark:text-orange-400 mt-0.5 shrink-0"
            />
            <div class="flex-1">
              <p
                class="text-sm font-medium text-orange-800 dark:text-orange-300"
              >
                {{ $t('ORDERS.REDIRECT.REDIRECTED_FROM') }}
                <button
                  class="font-mono text-n-brand hover:underline cursor-pointer"
                  @click="goToOrder(order.redirected_from_id)"
                >
                  #{{ order.redirected_from_id }}
                </button>
              </p>
              <p
                v-if="order.redirect_notes"
                class="text-xs text-orange-700 dark:text-orange-400/80 mt-1"
              >
                {{ order.redirect_notes }}
              </p>
            </div>
          </div>
        </div>

        <!-- Merged Children -->
        <section
          v-if="order.merged_children && order.merged_children.length > 0"
          class="mb-6"
        >
          <h3 class="text-sm font-medium text-n-slate-11 mb-3">
            {{ $t('ORDERS.MERGE.MERGED_CHILDREN') }}
          </h3>
          <div class="overflow-x-auto rounded-lg border border-n-weak">
            <table class="w-full text-sm">
              <thead class="bg-n-solid-3">
                <tr>
                  <th class="px-3 py-2 text-left text-xs text-n-slate-11">
                    {{ $t('ORDERS.MERGE.COLUMN_ID') }}
                  </th>
                  <th class="px-3 py-2 text-left text-xs text-n-slate-11">
                    {{ $t('ORDERS.MERGE.COLUMN_AQUASIS') }}
                  </th>
                  <th class="px-3 py-2 text-left text-xs text-n-slate-11">
                    {{ $t('ORDERS.MERGE.COLUMN_TICKET') }}
                  </th>
                  <th class="px-3 py-2 text-left text-xs text-n-slate-11">
                    {{ $t('ORDERS.MERGE.COLUMN_TYPE') }}
                  </th>
                  <th class="px-3 py-2 text-left text-xs text-n-slate-11">
                    {{ $t('ORDERS.MERGE.COLUMN_MERGED_AT') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="child in order.merged_children"
                  :key="child.id"
                  class="border-t border-n-weak hover:bg-n-alpha-1 cursor-pointer"
                  @click="goToOrder(child.id)"
                >
                  <td class="px-3 py-2 font-mono text-n-brand">
                    #{{ child.id }}
                  </td>
                  <td class="px-3 py-2 font-mono text-n-slate-12">
                    {{ child.aquasis_order_id || '-' }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-12">
                    {{ child.ticket_folio || '-' }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-12">
                    {{ child.order_type || '-' }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-11">
                    {{ formatDate(child.merged_at) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Order Information -->
        <section class="mb-6">
          <h3 class="text-sm font-medium text-n-slate-11 mb-3">
            {{ $t('ORDERS.DETAIL.ORDER_INFO') }}
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="p-3 rounded-lg bg-n-solid-3">
              <span class="text-xs text-n-slate-11">{{
                $t('ORDERS.DETAIL.ORDER_TYPE')
              }}</span>
              <p class="text-sm font-medium text-n-slate-12 mt-1">
                <template v-if="order.order_type_record">
                  {{ order.order_type_record.code }} -
                  {{ order.order_type_record.description }}
                </template>
                <template v-else>
                  {{ order.order_type || '-' }}
                </template>
              </p>
            </div>
            <div class="p-3 rounded-lg bg-n-solid-3">
              <span class="text-xs text-n-slate-11">{{
                $t('ORDERS.DETAIL.ORDER_MOTIVE')
              }}</span>
              <p class="text-sm font-medium text-n-slate-12 mt-1">
                <template v-if="order.order_motive">
                  {{ order.order_motive.code }} -
                  {{ order.order_motive.description }}
                </template>
                <template v-else> - </template>
              </p>
            </div>
            <div class="p-3 rounded-lg bg-n-solid-3">
              <span class="text-xs text-n-slate-11">{{
                $t('ORDERS.DETAIL.REPAIR_CODE')
              }}</span>
              <p class="text-sm font-medium text-n-slate-12 mt-1">
                <template v-if="order.repair_code_record">
                  {{ order.repair_code_record.code }} -
                  {{ order.repair_code_record.description }}
                </template>
                <template v-else>
                  {{ order.repair_code || '-' }}
                </template>
              </p>
            </div>
            <div class="p-3 rounded-lg bg-n-solid-3">
              <span class="text-xs text-n-slate-11">{{
                $t('ORDERS.DETAIL.REASON')
              }}</span>
              <p class="text-sm font-medium text-n-slate-12 mt-1">
                <template v-if="order.service_order_reason">
                  {{ order.service_order_reason.code }} -
                  {{ order.service_order_reason.description }}
                </template>
                <template v-else> - </template>
              </p>
            </div>
          </div>
        </section>

        <!-- Observations -->
        <section class="mb-6">
          <div class="flex items-center justify-between mb-2">
            <h3 class="text-sm font-medium text-n-slate-11">
              {{ $t('ORDERS.DETAIL.OBSERVATIONS') }}
            </h3>
            <button
              v-if="!isEditingObservations"
              class="p-1 rounded hover:bg-n-alpha-2 text-n-slate-10 hover:text-n-slate-12"
              @click="startEditingObservations"
            >
              <span class="i-lucide-pencil text-sm" />
            </button>
          </div>
          <div v-if="isEditingObservations" class="flex flex-col gap-2">
            <textarea
              v-model="editedObservations"
              rows="4"
              maxlength="80"
              class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand resize-none"
              :placeholder="$t('ORDERS.DETAIL.OBSERVATIONS_PLACEHOLDER')"
            />
            <span class="text-xs text-n-slate-10 text-right block">
              {{ editedObservations.length }}/80
            </span>
            <div class="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="xs"
                :disabled="isSavingObservations"
                @click="cancelEditingObservations"
              >
                {{ $t('ORDERS.DETAIL.CANCEL') }}
              </Button>
              <Button
                size="xs"
                color="blue"
                :is-loading="isSavingObservations"
                @click="saveObservations"
              >
                {{ $t('ORDERS.DETAIL.SAVE') }}
              </Button>
            </div>
          </div>
          <p v-else-if="order.observations" class="text-n-slate-12">
            {{ order.observations }}
          </p>
          <p v-else class="text-n-slate-10 italic">
            {{ $t('ORDERS.DETAIL.NO_OBSERVATIONS') }}
          </p>
        </section>

        <!-- Dates -->
        <section class="mb-6">
          <h3 class="text-sm font-medium text-n-slate-11 mb-3">
            {{ $t('ORDERS.DETAIL.DATES') }}
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="p-3 rounded-lg bg-n-solid-3">
              <span class="text-xs text-n-slate-11">{{
                $t('ORDERS.DETAIL.REQUEST_DATE')
              }}</span>
              <p class="text-sm font-medium text-n-slate-12 mt-1">
                {{ formatDate(order.request_date) }}
              </p>
            </div>
            <div class="p-3 rounded-lg bg-n-solid-3">
              <span class="text-xs text-n-slate-11">{{
                $t('ORDERS.DETAIL.CREATED_DATE')
              }}</span>
              <p class="text-sm font-medium text-n-slate-12 mt-1">
                {{ formatDate(order.created_at) }}
              </p>
            </div>
            <div class="p-3 rounded-lg bg-n-solid-3">
              <span class="text-xs text-n-slate-11">{{
                $t('ORDERS.DETAIL.UPDATED_DATE')
              }}</span>
              <p class="text-sm font-medium text-n-slate-12 mt-1">
                {{ formatDate(order.updated_at) }}
              </p>
            </div>
          </div>
        </section>

        <!-- Aquasis Error (shown independently) -->
        <div
          v-if="aquasisError && order.aquasis_order_id"
          class="mb-6 p-3 rounded-lg bg-n-ruby-3 text-n-ruby-11 text-sm"
        >
          {{ aquasisError }}
        </div>

        <!-- Aquasis Data Sections -->
        <template v-if="aquasisData && order.aquasis_order_id">
          <!-- Address & Location -->
          <section
            v-if="showSection('address') && aquasisData.otData"
            class="mb-6"
          >
            <h3 class="text-sm font-medium text-n-slate-11 mb-3">
              {{ $t('ORDERS.AQUASIS.ADDRESS') }}
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="p-3 rounded-lg bg-n-solid-3 md:col-span-2">
                <span class="text-xs text-n-slate-11">{{
                  $t('ORDERS.AQUASIS.FULL_ADDRESS')
                }}</span>
                <p class="text-sm font-medium text-n-slate-12 mt-1">
                  {{ aquasisData.otData.completeAddress || '-' }}
                </p>
              </div>
              <div class="p-3 rounded-lg bg-n-solid-3">
                <span class="text-xs text-n-slate-11">{{
                  $t('ORDERS.AQUASIS.DISTRICT')
                }}</span>
                <p class="text-sm font-medium text-n-slate-12 mt-1">
                  {{ aquasisData.otData.district || '-' }}
                </p>
              </div>
              <div class="p-3 rounded-lg bg-n-solid-3">
                <span class="text-xs text-n-slate-11">{{
                  $t('ORDERS.AQUASIS.POSTAL_CODE')
                }}</span>
                <p class="text-sm font-medium text-n-slate-12 mt-1">
                  {{ aquasisData.otData.postalCode || '-' }}
                </p>
              </div>
              <div
                v-if="
                  aquasisData.otData.geolocalization?.latitude &&
                  aquasisData.otData.geolocalization?.longitude
                "
                class="p-3 rounded-lg bg-n-solid-3"
              >
                <span class="text-xs text-n-slate-11">{{
                  $t('ORDERS.AQUASIS.GEOLOCATION')
                }}</span>
                <p class="text-sm font-medium text-n-slate-12 mt-1">
                  <a
                    :href="`https://maps.google.com/?q=${aquasisData.otData.geolocalization.latitude},${aquasisData.otData.geolocalization.longitude}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-n-brand hover:underline"
                  >
                    {{ aquasisData.otData.geolocalization.latitude }},
                    {{ aquasisData.otData.geolocalization.longitude }}
                  </a>
                </p>
              </div>
              <div class="p-3 rounded-lg bg-n-solid-3">
                <span class="text-xs text-n-slate-11">{{
                  $t('ORDERS.AQUASIS.CONTRACT')
                }}</span>
                <p class="text-sm font-medium text-n-slate-12 mt-1">
                  {{ aquasisData.otData.customerContract || '-' }}
                </p>
              </div>
            </div>
          </section>

          <!-- Client / Contact -->
          <section
            v-if="showSection('client') && aquasisData.clientData"
            class="mb-6"
          >
            <h3 class="text-sm font-medium text-n-slate-11 mb-3">
              {{ $t('ORDERS.AQUASIS.CLIENT_CONTACT') }}
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="p-3 rounded-lg bg-n-solid-3">
                <span class="text-xs text-n-slate-11">{{
                  $t('ORDERS.AQUASIS.CONTACT_NAME')
                }}</span>
                <p class="text-sm font-medium text-n-slate-12 mt-1">
                  {{ aquasisData.clientData.contactName || '-' }}
                </p>
              </div>
              <div class="p-3 rounded-lg bg-n-solid-3">
                <span class="text-xs text-n-slate-11">{{
                  $t('ORDERS.AQUASIS.PHONE_1')
                }}</span>
                <p class="text-sm font-medium text-n-slate-12 mt-1">
                  {{ aquasisData.clientData.contactTelephone1 || '-' }}
                </p>
              </div>
              <div class="p-3 rounded-lg bg-n-solid-3">
                <span class="text-xs text-n-slate-11">{{
                  $t('ORDERS.AQUASIS.PHONE_2')
                }}</span>
                <p class="text-sm font-medium text-n-slate-12 mt-1">
                  {{ aquasisData.clientData.contactTelephone2 || '-' }}
                </p>
              </div>
              <div class="p-3 rounded-lg bg-n-solid-3">
                <span class="text-xs text-n-slate-11">{{
                  $t('ORDERS.AQUASIS.CUSTOMER_TYPE')
                }}</span>
                <p class="text-sm font-medium text-n-slate-12 mt-1">
                  {{ aquasisData.clientData.customerType || '-' }}
                </p>
              </div>
              <div
                v-if="aquasisData.clientData.customerComments"
                class="p-3 rounded-lg bg-n-solid-3 md:col-span-2"
              >
                <span class="text-xs text-n-slate-11">
                  {{ $t('ORDERS.AQUASIS.CUSTOMER_COMMENTS') }}
                </span>
                <p class="text-sm font-medium text-n-slate-12 mt-1">
                  {{ aquasisData.clientData.customerComments }}
                </p>
              </div>
              <div
                v-if="
                  aquasisData.clientData.customerCiteFrom ||
                  aquasisData.clientData.customerCiteUntil
                "
                class="p-3 rounded-lg bg-n-solid-3 md:col-span-2"
              >
                <span class="text-xs text-n-slate-11">{{
                  $t('ORDERS.AQUASIS.APPOINTMENT_WINDOW')
                }}</span>
                <p class="text-sm font-medium text-n-slate-12 mt-1">
                  {{ formatDate(aquasisData.clientData.customerCiteFrom) }} —
                  {{ formatDate(aquasisData.clientData.customerCiteUntil) }}
                </p>
              </div>
            </div>
          </section>

          <!-- Meter / Equipment -->
          <section
            v-if="
              showSection('meter') &&
              ensureArray(aquasisData.otElements).length > 0
            "
            class="mb-6"
          >
            <h3 class="text-sm font-medium text-n-slate-11 mb-3">
              {{ $t('ORDERS.AQUASIS.METER_EQUIPMENT') }}
            </h3>
            <div
              v-for="(el, idx) in ensureArray(aquasisData.otElements)"
              :key="idx"
              class="mb-3"
            >
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div class="p-3 rounded-lg bg-n-solid-3">
                  <span class="text-xs text-n-slate-11">{{
                    $t('ORDERS.AQUASIS.SERIAL_NUMBER')
                  }}</span>
                  <p class="text-sm font-medium text-n-slate-12 mt-1">
                    {{ el.serialNumber || '-' }}
                  </p>
                </div>
                <div class="p-3 rounded-lg bg-n-solid-3">
                  <span class="text-xs text-n-slate-11">{{
                    $t('ORDERS.AQUASIS.BRAND')
                  }}</span>
                  <p class="text-sm font-medium text-n-slate-12 mt-1">
                    {{ el.meterBrandID || '-' }}
                  </p>
                </div>
                <div class="p-3 rounded-lg bg-n-solid-3">
                  <span class="text-xs text-n-slate-11">{{
                    $t('ORDERS.AQUASIS.MODEL')
                  }}</span>
                  <p class="text-sm font-medium text-n-slate-12 mt-1">
                    {{ el.meterModel || '-' }}
                  </p>
                </div>
                <div class="p-3 rounded-lg bg-n-solid-3">
                  <span class="text-xs text-n-slate-11">{{
                    $t('ORDERS.AQUASIS.GAUGE')
                  }}</span>
                  <p class="text-sm font-medium text-n-slate-12 mt-1">
                    {{ el.meterGauge || '-' }}
                  </p>
                </div>
                <div class="p-3 rounded-lg bg-n-solid-3">
                  <span class="text-xs text-n-slate-11">{{
                    $t('ORDERS.AQUASIS.DIGITS')
                  }}</span>
                  <p class="text-sm font-medium text-n-slate-12 mt-1">
                    {{ el.meterDial || '-' }}
                  </p>
                </div>
                <div class="p-3 rounded-lg bg-n-solid-3">
                  <span class="text-xs text-n-slate-11">
                    {{ $t('ORDERS.AQUASIS.INSTALLATION_DATE') }}
                  </span>
                  <p class="text-sm font-medium text-n-slate-12 mt-1">
                    {{ formatDate(el.installationDate) }}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <!-- Recent Readings -->
          <section
            v-if="
              showSection('readings') &&
              ensureArray(aquasisData.otReadings).length > 0
            "
            class="mb-6"
          >
            <h3 class="text-sm font-medium text-n-slate-11 mb-3">
              {{ $t('ORDERS.AQUASIS.RECENT_READINGS') }}
            </h3>
            <div class="overflow-x-auto rounded-lg border border-n-weak">
              <table class="w-full text-sm">
                <thead class="bg-n-solid-3">
                  <tr>
                    <th class="px-3 py-2 text-left text-xs text-n-slate-11">
                      {{ $t('ORDERS.AQUASIS.READING_DATE') }}
                    </th>
                    <th class="px-3 py-2 text-right text-xs text-n-slate-11">
                      {{ $t('ORDERS.AQUASIS.READING_REGISTER') }}
                    </th>
                    <th class="px-3 py-2 text-right text-xs text-n-slate-11">
                      {{ $t('ORDERS.AQUASIS.CONSUMED') }}
                    </th>
                    <th class="px-3 py-2 text-center text-xs text-n-slate-11">
                      {{ $t('ORDERS.AQUASIS.IS_BILLED') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(r, idx) in ensureArray(aquasisData.otReadings)"
                    :key="idx"
                    class="border-t border-n-weak"
                  >
                    <td class="px-3 py-2 text-n-slate-12">
                      {{ formatDate(r.readingDate) }}
                    </td>
                    <td class="px-3 py-2 text-right text-n-slate-12">
                      {{ r.readingRegister ?? '-' }}
                    </td>
                    <td class="px-3 py-2 text-right text-n-slate-12">
                      {{ r.consumed ?? '-' }}
                    </td>
                    <td class="px-3 py-2 text-center">
                      <span
                        class="px-2 py-0.5 text-xs rounded-full"
                        :class="
                          r.isBilled === 'true' || r.isBilled === true
                            ? 'bg-n-teal-3 text-n-teal-11'
                            : 'bg-n-solid-3 text-n-slate-10'
                        "
                      >
                        {{
                          r.isBilled === 'true' || r.isBilled === true
                            ? $t('ORDERS.AQUASIS.YES')
                            : $t('ORDERS.AQUASIS.NO')
                        }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- Unpaid Bills -->
          <section
            v-if="
              showSection('unpaidBills') &&
              ensureArray(aquasisData.otCustomerUnpaidBills).length > 0
            "
            class="mb-6"
          >
            <h3 class="text-sm font-medium text-n-slate-11 mb-3">
              {{ $t('ORDERS.AQUASIS.UNPAID_BILLS') }}
            </h3>
            <div class="overflow-x-auto rounded-lg border border-n-weak">
              <table class="w-full text-sm">
                <thead class="bg-n-solid-3">
                  <tr>
                    <th class="px-3 py-2 text-left text-xs text-n-slate-11">
                      {{ $t('ORDERS.AQUASIS.PERIOD') }}
                    </th>
                    <th class="px-3 py-2 text-left text-xs text-n-slate-11">
                      {{ $t('ORDERS.AQUASIS.BILL_STATUS') }}
                    </th>
                    <th class="px-3 py-2 text-right text-xs text-n-slate-11">
                      {{ $t('ORDERS.AQUASIS.AMOUNT') }}
                    </th>
                    <th class="px-3 py-2 text-center text-xs text-n-slate-11">
                      {{ $t('ORDERS.AQUASIS.RECLAIMED') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(b, idx) in ensureArray(
                      aquasisData.otCustomerUnpaidBills
                    )"
                    :key="idx"
                    class="border-t border-n-weak"
                  >
                    <td class="px-3 py-2 text-n-slate-12">
                      {{ b.yearPeriod || '-' }}
                    </td>
                    <td class="px-3 py-2 text-n-slate-12">
                      {{ b.statusBill || '-' }}
                    </td>
                    <td class="px-3 py-2 text-right text-n-slate-12">
                      {{ b.billImport ?? '-' }}
                    </td>
                    <td class="px-3 py-2 text-center">
                      <span
                        class="px-2 py-0.5 text-xs rounded-full"
                        :class="
                          b.reclaimed === 'true' || b.reclaimed === true
                            ? 'bg-n-amber-3 text-n-amber-11'
                            : 'bg-n-solid-3 text-n-slate-10'
                        "
                      >
                        {{
                          b.reclaimed === 'true' || b.reclaimed === true
                            ? $t('ORDERS.AQUASIS.YES')
                            : $t('ORDERS.AQUASIS.NO')
                        }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- Debt Summary -->
          <section
            v-if="showSection('debtSummary') && aquasisData.otCustomerDebt"
            class="mb-6"
          >
            <h3 class="text-sm font-medium text-n-slate-11 mb-3">
              {{ $t('ORDERS.AQUASIS.DEBT_SUMMARY') }}
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div class="p-3 rounded-lg bg-n-solid-3">
                <span class="text-xs text-n-slate-11">{{
                  $t('ORDERS.AQUASIS.TOTAL_DEBT')
                }}</span>
                <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                <p class="text-sm font-medium text-n-slate-12 mt-1">
                  ${{ aquasisData.otCustomerDebt.customerTotalDebt || '0' }}
                </p>
              </div>
              <div class="p-3 rounded-lg bg-n-solid-3">
                <span class="text-xs text-n-slate-11">
                  {{ $t('ORDERS.AQUASIS.RECLAIMED_BILLS') }}
                </span>
                <p class="text-sm font-medium text-n-slate-12 mt-1">
                  {{ aquasisData.otCustomerDebt.reclaimedBills || '0' }}
                </p>
              </div>
              <div class="p-3 rounded-lg bg-n-solid-3">
                <span class="text-xs text-n-slate-11">
                  {{ $t('ORDERS.AQUASIS.RECLAIMED_PERIOD') }}
                </span>
                <p class="text-sm font-medium text-n-slate-12 mt-1">
                  {{
                    aquasisData.otCustomerDebt.initialYearPeriodReclaimed || '-'
                  }}
                  —
                  {{
                    aquasisData.otCustomerDebt.finalYearPeriodReclaimed || '-'
                  }}
                </p>
              </div>
            </div>
          </section>

          <!-- Comments -->
          <section
            v-if="
              showSection('comments') &&
              ensureArray(aquasisData.otComments).length > 0
            "
            class="mb-6"
          >
            <h3 class="text-sm font-medium text-n-slate-11 mb-3">
              {{ $t('ORDERS.AQUASIS.COMMENTS') }}
            </h3>
            <div class="flex flex-col gap-2">
              <div
                v-for="(c, idx) in ensureArray(aquasisData.otComments)"
                :key="idx"
                class="p-3 rounded-lg bg-n-solid-3"
              >
                <div class="flex items-center justify-between mb-1">
                  <span class="text-xs font-medium text-n-slate-11">
                    {{ c.creationUser || $t('ORDERS.AQUASIS.SYSTEM_USER') }}
                  </span>
                  <span class="text-xs text-n-slate-10">
                    {{ formatDate(c.dateTimeComment) }}
                  </span>
                </div>
                <p class="text-sm text-n-slate-12">
                  {{ c.textComment }}
                </p>
              </div>
            </div>
          </section>
        </template>

        <!-- Aquasis Loading State -->
        <div
          v-else-if="isLoadingAquasis && order.aquasis_order_id"
          class="mb-6 flex items-center gap-2 text-sm text-n-slate-11"
        >
          <Spinner :size="16" />
          {{ $t('ORDERS.AQUASIS.LOADING') }}
        </div>
      </div>

      <!-- Sidebar -->
      <aside class="w-72 p-4 border-l border-n-weak bg-n-solid-2 overflow-auto">
        <!-- Status -->
        <section class="mb-4">
          <h4 class="text-xs font-medium text-n-slate-11 mb-2">
            {{ $t('ORDERS.DETAIL.SIDEBAR_STATUS') }}
          </h4>
          <select
            :value="order.status"
            :disabled="isUpdatingStatus"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
            @change="e => updateStatus(e.target.value)"
          >
            <option value="draft">{{ $t('ORDERS.STATUS_DRAFT') }}</option>
            <option value="ready">{{ $t('ORDERS.STATUS_READY') }}</option>
            <option value="dispatched">
              {{ $t('ORDERS.STATUS_DISPATCHED') }}
            </option>
            <option value="en_route">
              {{ $t('ORDERS.STATUS_EN_ROUTE') }}
            </option>
            <option value="on_site">
              {{ $t('ORDERS.STATUS_ON_SITE') }}
            </option>
            <option value="field_complete">
              {{ $t('ORDERS.STATUS_FIELD_COMPLETE') }}
            </option>
            <option value="redirected">
              {{ $t('ORDERS.STATUS_REDIRECTED') }}
            </option>
            <option
              v-if="isAdmin || isAquasisUnlinked || order.status === 'cancelled'"
              value="cancelled"
            >
              {{ $t('ORDERS.STATUS_CANCELLED') }}
            </option>
            <option v-if="order.status === 'merged'" value="merged">
              {{ $t('ORDERS.STATUS_MERGED') }}
            </option>
          </select>
        </section>

        <!-- Aquasis Integration -->
        <section v-if="order.aquasis_order_id" class="mb-4">
          <h4 class="text-xs font-medium text-n-slate-11 mb-2">
            {{ $t('ORDERS.DETAIL.SIDEBAR_AQUASIS') }}
          </h4>
          <div class="p-2 rounded-lg bg-n-solid-3">
            <span class="font-mono text-sm text-n-brand">
              {{ order.aquasis_order_id }}
            </span>
          </div>
        </section>

        <!-- Ticket -->
        <section v-if="order.ticket" class="mb-4">
          <h4 class="text-xs font-medium text-n-slate-11 mb-2">
            {{ $t('ORDERS.DETAIL.SIDEBAR_TICKET') }}
          </h4>
          <div class="flex flex-col gap-2">
            <div
              class="p-2 rounded-lg bg-n-solid-3 border border-n-weak cursor-pointer hover:bg-n-alpha-2 transition-colors"
              @click="goToTicket(order.ticket.display_id)"
            >
              <div class="flex items-center justify-between mb-1">
                <span class="font-mono text-sm text-n-brand">
                  {{ order.ticket.folio }}
                </span>
                <span
                  class="px-2 py-0.5 text-xs rounded-full"
                  :class="{
                    'bg-n-teal-9/10 text-n-teal-11':
                      order.ticket.status === 'open',
                    'bg-n-brand/10 text-n-blue-text':
                      order.ticket.status === 'in_progress',
                    'bg-n-amber-9/10 text-n-amber-11':
                      order.ticket.status === 'resolved',
                    'bg-n-slate-9/10 text-n-slate-11':
                      order.ticket.status === 'closed',
                  }"
                >
                  {{ order.ticket.status }}
                </span>
              </div>
              <p class="text-sm text-n-slate-12 font-medium">
                {{ order.ticket.title }}
              </p>
            </div>
          </div>
        </section>

        <!-- Linked Tickets -->
        <section v-if="order.linked_tickets?.length" class="mb-4">
          <h4 class="text-xs font-medium text-n-slate-11 mb-2">
            {{ $t('ORDERS.DETAIL.LINKED_TICKETS') }}
          </h4>
          <div class="flex flex-col gap-2">
            <div
              v-for="linkedTicket in order.linked_tickets"
              :key="linkedTicket.id"
              class="p-2 rounded-lg bg-n-solid-3 border border-n-weak cursor-pointer hover:bg-n-alpha-2 transition-colors"
              @click="goToTicket(linkedTicket.display_id)"
            >
              <div class="flex items-center justify-between mb-1">
                <span class="font-mono text-sm text-n-brand">
                  {{ linkedTicket.folio }}
                </span>
                <span
                  class="px-2 py-0.5 text-xs rounded-full"
                  :class="{
                    'bg-n-teal-9/10 text-n-teal-11':
                      linkedTicket.status === 'open',
                    'bg-n-brand/10 text-n-blue-text':
                      linkedTicket.status === 'in_progress',
                    'bg-n-amber-9/10 text-n-amber-11':
                      linkedTicket.status === 'resolved',
                    'bg-n-slate-9/10 text-n-slate-11':
                      linkedTicket.status === 'closed',
                  }"
                >
                  {{ linkedTicket.status }}
                </span>
              </div>
              <p class="text-sm text-n-slate-12 font-medium">
                {{ linkedTicket.title }}
              </p>
            </div>
          </div>
        </section>

        <!-- Contact -->
        <section v-if="order.contact" class="mb-4">
          <h4 class="text-xs font-medium text-n-slate-11 mb-2">
            {{ $t('ORDERS.DETAIL.SIDEBAR_CONTACT') }}
          </h4>
          <div class="flex items-center gap-2 p-2 rounded-lg bg-n-solid-3">
            <Avatar :name="order.contact.name" :size="40" />
            <div class="flex flex-col">
              <span class="font-medium text-n-slate-12">
                {{ order.contact.name }}
              </span>
              <span v-if="order.contact.email" class="text-xs text-n-slate-11">
                {{ order.contact.email }}
              </span>
            </div>
          </div>
        </section>

        <!-- Details -->
        <section>
          <h4 class="text-xs font-medium text-n-slate-11 mb-2">
            {{ $t('ORDERS.DETAIL.SIDEBAR_DETAILS') }}
          </h4>
          <div class="flex flex-col gap-2 text-sm">
            <div class="flex justify-between items-center">
              <span class="text-n-slate-11">{{
                $t('ORDERS.DETAIL.SIDEBAR_CLASS')
              }}</span>
              <span
                :class="orderClassColor"
                class="px-2 py-0.5 text-xs font-medium rounded"
              >
                {{ orderClassBadge }} - {{ orderClassLabel }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-n-slate-11">{{
                $t('ORDERS.DETAIL.SIDEBAR_TYPE')
              }}</span>
              <span class="text-n-slate-12">
                {{ order.order_type_record?.code || order.order_type || '-' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-n-slate-11">{{
                $t('ORDERS.DETAIL.SIDEBAR_REPAIR_CODE')
              }}</span>
              <span class="text-n-slate-12">
                {{ order.repair_code_record?.code || order.repair_code || '-' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-n-slate-11">{{
                $t('ORDERS.DETAIL.SIDEBAR_REQUEST_DATE')
              }}</span>
              <span class="text-n-slate-12">
                {{ formatDate(order.request_date) }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-n-slate-11">{{
                $t('ORDERS.DETAIL.SIDEBAR_CREATED')
              }}</span>
              <span class="text-n-slate-12">
                {{ formatDate(order.created_at) }}
              </span>
            </div>
          </div>
        </section>
      </aside>
    </div>
  </div>

  <!-- Not Found -->
  <div v-else class="flex flex-col items-center justify-center w-full h-full">
    <span class="i-lucide-package-x text-6xl text-n-slate-10 mb-4" />
    <p class="text-n-slate-11 mb-4">{{ $t('ORDERS.DETAIL.NOT_FOUND') }}</p>
    <Button variant="outline" @click="goBack">
      {{ $t('ORDERS.DETAIL.BACK_TO_ORDERS') }}
    </Button>
  </div>

  <!-- Modals -->
  <ReportVisitModal
    v-if="order"
    :show="showReportVisitModal"
    :order="order"
    @close="showReportVisitModal = false"
    @reported="onVisitReported"
  />
  <ResolveOrderModal
    v-if="order"
    :show="showResolveOrderModal"
    :order="order"
    :aquasis-data="aquasisData"
    :field-visibility="visibleSections"
    @close="showResolveOrderModal = false"
    @resolved="onOrderResolved"
  />
  <CancelOrderDialog
    v-if="order"
    :show="showCancelOrderDialog"
    :order="order"
    :aquasis-data="aquasisData"
    @close="showCancelOrderDialog = false"
    @cancelled="onOrderCancelled"
  />
  <MergeOrderModal
    v-if="order"
    :show="showMergeOrderModal"
    :primary-order="order"
    @close="showMergeOrderModal = false"
    @merged="onOrderMerged"
  />
  <RedirectOrderModal
    v-if="order"
    :show="showRedirectOrderModal"
    :order="order"
    :aquasis-data="aquasisData"
    @close="showRedirectOrderModal = false"
    @redirected="onOrderRedirected"
  />
</template>
